import { useNavigate } from 'react-router-dom'

import { Box, Icon, Section, Text } from '@factorialco/deprecated-design-system'

import { useNavigation } from 'modules/core/hooks/useNavigation'
import i18n from 'modules/core/lib/i18n'

import integrationCodes from 'modules/marketplace/lib/integrationCodes'

interface IntegrationBoxProps {
  linkName: string
  destinationPath: string
  isLastElement?: boolean
}
const IntegrationBox = ({ destinationPath, linkName, isLastElement }: IntegrationBoxProps) => {
  const navigate = useNavigate()

  return (
    <Box
      cursor="pointer"
      onClick={() => navigate(destinationPath)}
      border={
        !isLastElement
          ? {
              bottom: {
                color: 'grey400',
                width: 's1',
                style: 'solid',
              },
            }
          : {}
      }
      gap="s8"
      flexDirection="row"
      alignItems="center"
      justifyContent="spaceBetween"
      padding="s16"
    >
      <Text.Normal color="grey1000" weight="normal">
        {linkName}
      </Text.Normal>
      <Box
        padding="s2"
        border={{
          all: {
            color: 'grey400',
            width: 's1',
            style: 'solid',
          },
        }}
        borderRadius={{
          all: 'rel500',
        }}
      >
        <Icon.Medium icon="arrow-right" />
      </Box>
    </Box>
  )
}

interface CompanyInstallationProps {
  integrationDatoId: string
}

interface ContentProps {
  installedIntegrations: Array<Required<CompanyInstallationProps>>
}

const Content = ({ installedIntegrations }: ContentProps) => {
  const { navigation } = useNavigation()

  const hasA3nomInstalled = () =>
    installedIntegrations.find(
      (installation) => installation.integrationDatoId === integrationCodes.a3nom
    )
  const hasDatevInstalled = () =>
    installedIntegrations.find(
      (installation) => installation.integrationDatoId === integrationCodes.datev
    )
  const hasDatevApiInstalled = () =>
    installedIntegrations.find(
      (installation) => installation.integrationDatoId === integrationCodes.datev_api
    )
  const hasDatevLugApiInstalled = () =>
    installedIntegrations.find(
      (installation) => installation.integrationDatoId === integrationCodes.datev_lug_api
    )
  const hasZucchettiInstalled = () =>
    installedIntegrations.find(
      (installation) => installation.integrationDatoId === integrationCodes.zucchetti
    )
  const hasPaierhInstalled = () =>
    installedIntegrations.find(
      (installation) => installation.integrationDatoId === integrationCodes.paierh
    )
  const hasYeapPaierhInstalled = () =>
    installedIntegrations.find(
      (installation) => installation.integrationDatoId === integrationCodes.yeap_paierh
    )
  const hasSilaeInstalled = () =>
    installedIntegrations.find(
      (installation) => installation.integrationDatoId === integrationCodes.silae
    )
  const hasGispagheInstalled = () =>
    installedIntegrations.find(
      (installation) => installation.integrationDatoId === integrationCodes.gispaghe
    )

  const integrations = [
    {
      check: hasA3nomInstalled,
      linkName: i18n.t('app_store_v2.app_integrations.payroll_integrations_a3nom.general.title'),
      destinationPath: navigation.settings.compensations.integrationA3nom.path,
    },
    {
      check: hasDatevInstalled,
      linkName: i18n.t('app_store_v2.app_integrations.payroll_integrations_datev.general.title'),
      destinationPath: navigation.settings.compensations.integrationDatev.path,
    },
    {
      check: () => hasDatevApiInstalled() || hasDatevLugApiInstalled(),
      linkName: i18n.t(
        'app_store_v2.app_integrations.payroll_integrations_datev_api.general.title'
      ),
      destinationPath: navigation.settings.compensations.integrationDatevApi.path,
    },
    {
      check: hasZucchettiInstalled,
      linkName: i18n.t(
        'app_store_v2.app_integrations.payroll_integrations_zucchetti.general.title'
      ),
      destinationPath: navigation.settings.compensations.integrationZucchetti.path,
    },
    {
      check: hasPaierhInstalled,
      linkName: i18n.t('app_store_v2.app_integrations.payroll_integrations_paierh.general.title'),
      destinationPath: navigation.settings.compensations.integrationPaierh.path,
    },
    {
      check: hasYeapPaierhInstalled,
      linkName: i18n.t(
        'app_store_v2.app_integrations.payroll_integrations_yeap_paierh.general.title'
      ),
      destinationPath: navigation.settings.compensations.integrationYeapPaierh.path,
    },
    {
      check: hasSilaeInstalled,
      linkName: i18n.t('app_store_v2.app_integrations.payroll_integrations_silae.general.title'),
      destinationPath: navigation.settings.compensations.integrationSilae.path,
    },
    {
      check: hasGispagheInstalled,
      linkName: i18n.t('app_store_v2.app_integrations.payroll_integrations_gispaghe.general.title'),
      destinationPath: navigation.settings.compensations.integrationGispaghe.path,
    },
  ]

  const integrationsAvailable = [
    hasA3nomInstalled,
    hasDatevInstalled,
    hasDatevApiInstalled,
    hasDatevLugApiInstalled,
    hasZucchettiInstalled,
    hasPaierhInstalled,
    hasYeapPaierhInstalled,
    hasSilaeInstalled,
    hasGispagheInstalled,
  ]

  const integrationsInstalled = integrationsAvailable.filter((func) => func()).length

  return (
    <Box
      border={
        integrationsInstalled > 0
          ? {
              all: {
                color: 'grey400',
                width: 's1',
                style: 'solid',
              },
            }
          : {}
      }
      borderRadius={{
        all: 'abs016',
      }}
    >
      {integrations.map(({ check, linkName, destinationPath }, index) => {
        const isLastElement = index === integrations.length - 1

        if (check()) {
          return (
            <IntegrationBox
              key={linkName}
              linkName={linkName}
              destinationPath={destinationPath}
              isLastElement={isLastElement}
            />
          )
        }
      })}
    </Box>
  )
}

interface IntegrationsProps {
  installedIntegrations: Array<Required<CompanyInstallationProps>>
}

const Integrations = ({ installedIntegrations }: IntegrationsProps) => {
  return (
    <Section
      title={i18n.t('app_store_v2.apps.payroll_bundle.settings.integrations.title')}
      illustration="URL"
      description={i18n.t('app_store_v2.apps.payroll_bundle.settings.integrations.description')}
    >
      <Content installedIntegrations={installedIntegrations} />
    </Section>
  )
}

export default Integrations
